import { categoricalColorSchemes } from '@nivo/colors'
import { scaleOrdinal } from 'd3-scale'
import { parse } from 'date-fns'
import moment from 'moment'
import { startTransition, useCallback, useMemo, useState } from 'react'

export const parseDate = (date: string) => parse(date, 'yyyy-MM-dd', new Date())

export const colorScale = scaleOrdinal(categoricalColorSchemes.dark2)

export const useNormalizedData = (
  rawData: {
    date_start: string
    date_end: string
    specimens_processed: number | null
    value: number | null
    disease_name: string
    country_alpha_3: string
    name: string
    source: number
  }[]
) => {
  // Make data nicer
  const processedData = useMemo(
    () =>
      rawData
        .filter((x) => x.value !== undefined && x.value !== null)
        .map((x) => ({
          date: x.date_end,
          value: parseFloat(x.value?.toString() ?? ''),
          diseaseName: x.disease_name,
          countryAlpha3: x.country_alpha_3,
          numOfTests: x.specimens_processed ?? 0,
          name: x.name,
          source: x.source,
        })),
    [rawData]
  )

  // Aggregate data by disease, country, and date
  const aggregatedData = useMemo(() => {
    const groupedData = new Map<
      string,
      {
        date: string
        value: number
        diseaseName: string
        countryAlpha3: string
        numOfTests: number
        name: string
        source: number
        sources: number[]
      }
    >()

    processedData.forEach((item) => {
      const key = `${item.diseaseName}-${item.countryAlpha3}-${item.date}`

      if (groupedData.has(key)) {
        const existing = groupedData.get(key)!
        existing.value += item.value
        existing.numOfTests += item.numOfTests
        existing.sources.push(item.source)
      } else {
        groupedData.set(key, {
          date: item.date,
          value: item.value,
          diseaseName: item.diseaseName,
          countryAlpha3: item.countryAlpha3,
          numOfTests: item.numOfTests,
          name: item.name,
          source: item.source,
          sources: [item.source], // Array of all sources for reference
        })
      }
    })

    return Array.from(groupedData.values())
  }, [processedData])

  // Sort it
  return aggregatedData.sort(
    (a, b) => parseDate(a.date).getTime() - parseDate(b.date).getTime()
  )
}

export const useDraggableChart = ({
  changeDateRange,
}: {
  changeDateRange: (start: string, end: string) => void
}) => {
  const [isSelecting, setIsSelecting] = useState(false)
  const [refAreaLeft, setRefAreaLeft] = useState<string | null>(null)
  const [refAreaRight, setRefAreaRight] = useState<string | null>(null)

  const handleMouseDown = useCallback((e: any) => {
    if (e.activeLabel) {
      setRefAreaLeft(e.activeLabel)
      setIsSelecting(true)
    }
  }, [])

  const handleMouseMove = useCallback(
    (e: any) => {
      if (isSelecting && e.activeLabel) {
        setRefAreaRight(e.activeLabel)
      }
    },
    [isSelecting]
  )

  const handleMouseUp = useCallback(() => {
    if (refAreaLeft && refAreaRight) {
      const [left, right] = [refAreaLeft, refAreaRight].sort()
      startTransition(() => {
        changeDateRange(
          moment(left).format('YYYY-MM-DD'),
          moment(right).format('YYYY-MM-DD')
        )
      })
    }
    setRefAreaLeft(null)
    setRefAreaRight(null)
    setIsSelecting(false)
  }, [changeDateRange, refAreaLeft, refAreaRight])

  return {
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    refAreaLeft,
    refAreaRight,
  }
}
